import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Check, Pencil, Plus } from "lucide-react";
import { IconButton, Typography } from "@akuna/ui";
import { cn } from "@/lib/utils";

/**
 * Cada seção tem um tom fixo: é o que deixa a ficha varrível de relance dentro
 * de um modal que já carrega comanda e dados da reserva.
 */
export type PetProfileSectionTone = "amber" | "blue" | "danger" | "purple" | "success" | "orange";

const TONE_ICON: Record<PetProfileSectionTone, string> = {
  amber: "bg-amber-100 text-amber-800",
  blue: "bg-blue-100 text-blue-700",
  danger: "bg-state-danger-soft text-state-danger",
  purple: "bg-purple-100 text-purple-700",
  success: "bg-state-success-soft text-state-success",
  orange: "bg-orange-100 text-akuna-orange",
};

export type PetProfileSectionShellProps = {
  title: string;
  icon: LucideIcon;
  tone: PetProfileSectionTone;
  count?: number;
  isEditing?: boolean;
  onToggleEdit?: () => void;
  editLabel?: string;
  doneLabel?: string;
  onAdd?: () => void;
  addLabel?: string;
  fullWidth?: boolean;
  compact?: boolean;
  children: ReactNode;
};

export function PetProfileSectionShell({
  title,
  icon: Icon,
  tone,
  count,
  isEditing,
  onToggleEdit,
  editLabel,
  doneLabel,
  onAdd,
  addLabel,
  fullWidth,
  compact = false,
  children,
}: PetProfileSectionShellProps) {
  // Editar sempre ganha a linha inteira: os controles precisam de espaço e a
  // expansão sinaliza qual seção está aberta.
  const spansBothColumns = Boolean(fullWidth || isEditing);

  return (
    <section
      className={cn(
        "group grid content-start gap-0.5",
        !(compact && !isEditing) && "rounded-lg border border-border/60 bg-card p-2 shadow-akuna-xs transition-shadow",
        isEditing && "border-akuna-orange/40 shadow-akuna-sm",
        spansBothColumns && "sm:col-span-2",
      )}
    >
      <header className="flex items-center">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-xl text-primary">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <Typography variant="body">
          <span className="font-medium">{title}</span>
        </Typography>
        {typeof count === "number" && count > 0 ? (
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
            {count}
          </span>
        ) : null}
        {onAdd || onToggleEdit ? (
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {onAdd ? (
              <IconButton
                variant="ghost"
                size="icon-sm"
                label={addLabel ?? ""}
                onClick={onAdd}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
              </IconButton>
            ) : null}
            {onToggleEdit ? (
              <IconButton
                variant={isEditing ? "secondary" : "ghost"}
                size="icon-sm"
                className={cn(
                  "transition-opacity",
                  !isEditing && "opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100",
                )}
                label={isEditing ? (doneLabel ?? "") : (editLabel ?? "")}
                onClick={onToggleEdit}
              >
                {isEditing ? (
                  <Check className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                )}
              </IconButton>
            ) : null}
          </div>
        ) : null}
      </header>
      {children}
    </section>
  );
}

/** Vazio consistente entre as seções: discreto, nunca um bloco pesado. */
export function PetProfileSectionEmpty({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}
